// DeliveryAppBranding.js — the customer app's name, contact details, brand
// colour, and the "invite customers to install it" switch.
//
// Deliberately its OWN component with its own load/save, not folded into
// OrgSettings.js's embedded mode or wired into the dialog's shared "Save
// changes" button. Two reasons:
//   1. It talks to a different endpoint (`/manage/branding`) than
//      OrgSettings' `/api/business/settings` — no data overlap, no reason
//      to share a save path.
//   2. `patientAppSettings` (see OrgSettingsPanels.js's PatientAppSection /
//      defaultPatientAppSettings) is a similarly-named but ENTIRELY
//      DIFFERENT, older settings blob — from the pharmacy PatientSmart
//      product this platform was rewritten from, not this delivery
//      platform's own customer app. Keeping this as a fully separate,
//      standalone-save component avoids any risk of the two colliding.
//
// Matches OrgSettings.js's own "standalone" pattern (loading/ready/saving/
// saved/error status, its own Save button) rather than its "embedded" one.
import API_BASE from "./apiBase";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";

const API = `${API_BASE}`;

function authHeaders() {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

// The backend nests errors as { error: { code, message } } (see
// middleware/errorHandler.js) — NOT a flat { error: "..." } string. Reading
// `data.error` directly, as this file used to, silently produces
// "[object Object]" instead of the actual message. Organisation.js's own
// `api()` helper already unpacks it correctly (`data.error.message`); this
// mirrors that.
function errorMessage(data, status) {
  if (data && data.error && typeof data.error === "object" && data.error.message) {
    return data.error.message;
  }
  if (data && typeof data.error === "string") return data.error; // defensive, in case of future flat errors
  return `HTTP ${status}`;
}

const label = { fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: 0.3 };
const help = { fontSize: 11, color: "#6b7280", marginTop: 4, lineHeight: 1.4, maxWidth: 480 };
const section = { border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px", marginBottom: 16, background: "#fff" };
const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export default function DeliveryAppBranding({ embedded = false } = {}) {
  const [companyName, setCompanyName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [primaryHex, setPrimaryHex] = useState("");
  const [publicCode, setPublicCode] = useState("");
  const [inviteToApp, setInviteToApp] = useState(false);
  const [status, setStatus] = useState("loading"); // loading | ready | saving | saved | error
  const [error, setError] = useState(null);

  const loadedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/manage/branding`, { headers: authHeaders() });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(errorMessage(data, res.status));
        setCompanyName(data.company_name || "");
        setContactPhone(data.contact_phone || "");
        setContactEmail(data.contact_email || "");
        setPrimaryHex(data.primary_hex || "");
        setPublicCode(data.public_code || "");
        setInviteToApp(Boolean(data.invite_to_app));
        loadedRef.current = true;
        setStatus("ready");
      } catch (e) {
        setError(e.message);
        setStatus("error");
      }
    })();
  }, []);

  const save = async () => {
    setStatus("saving");
    setError(null);
    const hexTrimmed = primaryHex.trim();
    if (hexTrimmed && !HEX_PATTERN.test(hexTrimmed)) {
      setError("Brand colour must look like #RRGGBB.");
      setStatus("error");
      return;
    }
    try {
      const res = await fetch(`${API}/manage/branding`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          company_name: companyName.trim(),
          contact_phone: contactPhone.trim(),
          contact_email: contactEmail.trim(),
          primary_hex: hexTrimmed,
          public_code: publicCode.trim(),
          invite_to_app: inviteToApp,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(data, res.status));
      setStatus("saved");
      setTimeout(() => setStatus("ready"), 1800);
    } catch (e) {
      setError(e.message);
      setStatus("error");
    }
  };

  if (status === "loading") {
    return <div style={{ padding: 24, color: "#6b7280" }}>Loading delivery app settings…</div>;
  }

  return (
    <div style={embedded ? { padding: 0, maxWidth: 560 } : { padding: "16px 20px", maxWidth: 560 }}>
      {!embedded && <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Delivery app</h2>}
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        What customers see in the app, and whether they're invited to install it.
        None of this restricts what a customer can do once signed in — every screen
        is visible to every customer; this only changes what they're shown and told.
      </div>
      {error && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}

      <div style={section}>
        <div style={{ ...label, marginBottom: 10 }}>How you're shown to customers</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <TextField
            label="Company name"
            size="small"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Contact phone"
            size="small"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            fullWidth
          />
          <TextField
            label="Contact email"
            size="small"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            fullWidth
          />
          <TextField
            label="Brand colour"
            size="small"
            value={primaryHex}
            onChange={(e) => setPrimaryHex(e.target.value)}
            placeholder="#026FD7"
            helperText="RRGGBB hex, e.g. #026FD7. Leave blank to let each customer pick their own."
          />
        </div>
      </div>

      <div style={section}>
        <div style={{ ...label, marginBottom: 10 }}>Inviting customers to the app</div>
        <FormControlLabel
          control={
            <Checkbox
              checked={inviteToApp}
              onChange={(e) => setInviteToApp(e.target.checked)}
            />
          }
          label="Invite customers to use the delivery app"
        />
        <div style={help}>
          When on, every email a customer gets — a delivery update or a message
          you send them — carries a short paragraph inviting them to install the
          app, with a QR code and an activation code, until they actually
          activate it. Customers who've already installed it stop being invited.
        </div>

        <div style={{ marginTop: 14 }}>
          <TextField
            label="App install code"
            size="small"
            value={publicCode}
            onChange={(e) => setPublicCode(e.target.value.replace(/\s+/g, ""))}
            placeholder="e.g. acme-pharmacy"
          />
          <div style={help}>
            Letters, numbers and hyphens only. This is what the app is built
            with (or what a customer types) to find your organisation when
            they ask for a fresh code — not a secret, since it goes out on
            every invitation. Changing it after customers have unread
            invitations in their inbox will break those specific links; new
            invitations always use the current code.
          </div>
        </div>
      </div>

      {!embedded && (
        <Button
          onClick={save}
          disabled={status === "saving"}
          variant="contained"
          sx={{ mt: 1 }}
        >
          {status === "saving" ? "Saving…" : status === "saved" ? "✓ Saved" : "Save delivery app settings"}
        </Button>
      )}
    </div>
  );
}
